import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Product {
  name: string;
  price: number;
  image: string;
}

interface Category {
  category: string;
  categoryKey: string;
  icon: string;
  products: Product[];
}

interface InfiniteCategorySliderProps {
  categories: Category[];
  selectedPlan: 'bronce' | 'plata' | 'oro';
  discountRates: { [key: string]: { [key: string]: number } };
  formatPrice: (price: number) => string;
  calculateDiscountedPrice: (price: number, category: string, plan: string) => number;
}

export const InfiniteCategorySlider: React.FC<InfiniteCategorySliderProps> = ({
  categories,
  selectedPlan,
  discountRates,
  formatPrice,
  calculateDiscountedPrice,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      // Check initial state after a brief delay to ensure layout is complete
      const timer = setTimeout(() => {
        updateScrollButtons();
      }, 100);
      el.addEventListener('scroll', updateScrollButtons);
      return () => {
        clearTimeout(timer);
        el.removeEventListener('scroll', updateScrollButtons);
      };
    }
  }, [categories, isMobile]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const planColors = {
    bronce: { 
      bg: 'bg-amber-700/20', 
      border: 'border-amber-700/50', 
      text: 'text-amber-600',
      gradient: 'from-amber-700 to-amber-900',
      priceText: 'text-amber-500',
      badgeBg: 'bg-amber-700',
      badgeText: 'text-white'
    },
    plata: { 
      bg: 'bg-slate-400/20', 
      border: 'border-slate-500/50', 
      text: 'text-slate-400',
      gradient: 'from-slate-400 to-slate-600',
      priceText: 'text-slate-300',
      badgeBg: 'bg-slate-400',
      badgeText: 'text-black'
    },
    oro: { 
      bg: 'bg-yellow-500/20', 
      border: 'border-yellow-500/50', 
      text: 'text-yellow-500',
      gradient: 'from-yellow-400 to-amber-600',
      priceText: 'text-yellow-400',
      badgeBg: 'bg-gradient-to-r from-yellow-400 to-amber-500',
      badgeText: 'text-black'
    },
  };

  const colors = planColors[selectedPlan];

  return (
    <div className="relative">
      {/* Scroll Buttons - hidden when can't scroll */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-zinc-800/90 border border-zinc-600 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors shadow-lg -ml-2 md:ml-0"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-zinc-800/90 border border-zinc-600 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors shadow-lg -mr-2 md:mr-0"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Gradient masks - only show when can scroll */}
      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      )}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
      )}
      
      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto px-0 md:px-8 py-6 scroll-smooth items-center snap-x snap-mandatory"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pan-y',
          paddingLeft: isMobile ? 'calc((100vw - min(320px, 85vw)) / 2)' : undefined,
          paddingRight: isMobile ? 'calc((100vw - min(320px, 85vw)) / 2)' : undefined,
        }}
      >
        {categories.map((category, catIndex) => (
          <div
            key={`${category.category}-${catIndex}`}
            className="flex-shrink-0 w-[85vw] max-w-[320px] md:w-[340px] flex items-center snap-center"
          >
            <div className={`
              bg-zinc-900/80 rounded-xl overflow-hidden border ${colors.border}
              shadow-[6px_6px_12px_rgba(0,0,0,0.4),-3px_-3px_8px_rgba(255,255,255,0.03)]
              hover:shadow-[8px_8px_16px_rgba(0,0,0,0.5),-4px_-4px_12px_rgba(255,255,255,0.05)]
              transition-all duration-300 h-full
            `}>
              {/* Category Header - Single row aligned */}
              <div className={`px-4 py-5 bg-gradient-to-r ${colors.gradient} flex flex-row items-center justify-between gap-2`}>
                <div className="flex flex-row items-center gap-3">
                  <span className="text-2xl leading-none flex items-center">{category.icon}</span>
                  <h3 className="text-lg font-bold text-white leading-none translate-y-0.5">{category.category}</h3>
                </div>
                <Badge className={`${colors.badgeBg} ${colors.badgeText} border-0 font-bold text-sm px-3 py-1.5 whitespace-nowrap`}>
                  {discountRates[selectedPlan]?.[category.categoryKey] || discountRates[selectedPlan]?.[category.category] || 0}% OFF
                </Badge>
              </div>
              
              {/* Products - Compact on mobile */}
              <div className="p-4 space-y-3">
                {category.products.slice(0, isMobile ? 2 : 3).map((product, prodIndex) => {
                  const discountedPrice = calculateDiscountedPrice(product.price, category.categoryKey || category.category, selectedPlan);
                  const savings = product.price - discountedPrice;
                  
                  return (
                    <div
                      key={`${product.name}-${prodIndex}`}
                      className={`rounded-lg bg-zinc-800/50 ${isMobile ? 'p-3' : 'flex items-center gap-3 p-2'}`}
                    >
                      {/* Mobile: Vertical layout */}
                      {isMobile ? (
                        <>
                          {/* Text first on mobile */}
                          <div className="mb-3">
                            <h4 className="text-white font-semibold text-base leading-tight">{product.name}</h4>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-red-400 line-through text-base font-medium">{formatPrice(product.price)}</span>
                              <span className={`font-bold text-xl ${colors.priceText}`}>{formatPrice(discountedPrice)}</span>
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-sm px-2 py-0.5">
                                -{formatPrice(savings)}
                              </Badge>
                            </div>
                          </div>
                          {/* Image below on mobile - LARGER */}
                          <div className="w-full h-48 rounded-lg overflow-hidden bg-zinc-700">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Desktop: Horizontal layout */}
                          <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-700">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-medium text-sm leading-tight truncate">{product.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-zinc-500 line-through text-xs">{formatPrice(product.price)}</span>
                              <span className={`font-bold text-sm ${colors.priceText}`}>{formatPrice(discountedPrice)}</span>
                            </div>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs flex-shrink-0">
                            -{formatPrice(savings)}
                          </Badge>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
